import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { Box, Group } from '@mantine/core'
import { AppBadge, AppButton, AppSkeleton, AppTabs, AppText } from '../../app/common/ui'
import { FiSearch, FiX } from 'react-icons/fi'
import { IoImageOutline, IoPersonOutline, IoCogOutline, IoDocumentTextOutline, IoConstructOutline } from 'react-icons/io5'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../app/stores/store'
import agent from '../../app/api/agent'
import type {
  SearchResultDto,
  FirearmModelSearchItem,
  PartSearchItem,
  PostSearchItem,
  UserSearchItem,
  BuildSearchItem,
  CartridgeSpecSearchItem,
  AmmoProductSearchItem
} from '../../app/models/search'
import type { TagDto } from '../../app/models/tag'
import EmptyState from '../../app/common/ui/EmptyState'
import AmmoScoreBadge from '../../app/common/ui/AmmoScoreBadge'
import AuthGateCta from '../../app/common/ui/AuthGateCta'
import GunSlopWordmark from '../../app/common/ui/GunSlopWordmark'
import { postTypeLabel, POST_TYPE_TOKEN_COLORS } from '@gunslop/ui'

// ─── Recent-search helpers ─────────────────────────────────────────────────────

const RECENT_KEY = 'gs_recent_searches'
const MAX_RECENT = 8

function getRecentSearches (): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
  } catch {
    return []
  }
}

function saveRecentSearch (q: string) {
  const trimmed = q.trim()
  if (!trimmed) return
  const prev = getRecentSearches().filter((s) => s.toLowerCase() !== trimmed.toLowerCase())
  const next = [trimmed, ...prev].slice(0, MAX_RECENT)
  localStorage.setItem(RECENT_KEY, JSON.stringify(next))
}

function removeRecentSearch (q: string) {
  const next = getRecentSearches().filter((s) => s !== q)
  localStorage.setItem(RECENT_KEY, JSON.stringify(next))
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function relativeDate (iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

function trunc (s: string | null | undefined, n: number): string {
  if (!s) return ''
  return s.length > n ? s.slice(0, n) + '…' : s
}

function initials (displayName: string | null | undefined, username: string): string {
  const name = displayName || username || '?'
  return name.charAt(0).toUpperCase()
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function GroupHeader ({ label, count }: { label: string; count: number }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, marginTop: 28 }}>
      <AppText
        sx={{
          fontFamily: 'Oswald, sans-serif',
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: 'var(--gs-muted)',
          whiteSpace: 'nowrap'
        }}
      >
        {label}
      </AppText>
      <Box sx={{ flex: 1, height: 1, background: 'var(--gs-border)' }} />
      <Box
        sx={{
          background: 'rgba(232,93,4,0.15)',
          border: '1px solid rgba(232,93,4,0.35)',
          color: 'var(--gs-accent)',
          padding: '1px 9px',
          borderRadius: 10,
          fontSize: 11,
          fontWeight: 700,
          fontFamily: 'DM Sans, sans-serif'
        }}
      >
        {count}
      </Box>
    </Box>
  )
}

function PillBadge ({
  children,
  color = 'var(--gs-blue)'
}: {
  children: React.ReactNode;
  color?: string;
}) {
  return (
    <span
      style={{
        background: `${color}22`,
        color,
        border: `1px solid ${color}55`,
        padding: '1px 7px',
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 600,
        fontFamily: 'DM Sans, sans-serif',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        whiteSpace: 'nowrap'
      }}
    >
      {children}
    </span>
  )
}

function Thumb ({ src, icon }: { src?: string | null; icon?: React.ReactNode }) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
      />
    )
  }
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 4,
        flexShrink: 0,
        background: 'var(--gs-surface2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--gs-muted)'
      }}
    >
      {icon ?? <IoImageOutline size={18} />}
    </Box>
  )
}

function ResultCard ({
  onClick,
  children
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false)
  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: 'var(--gs-surface)',
        border: `1px solid ${hovered ? 'var(--gs-accent)' : 'var(--gs-border)'}`,
        borderRadius: 6,
        padding: '10px 14px',
        marginBottom: 6,
        cursor: 'pointer',
        transition: 'border-color 0.15s'
      }}
    >
      {children}
    </Box>
  )
}

function FirearmCard ({ item, onClick }: { item: FirearmModelSearchItem; onClick: () => void }) {
  return (
    <ResultCard onClick={onClick}>
      <Thumb src={item.coverImageUrl} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AppText
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--gs-text)',
            lineHeight: 1.3
          }}
        >
          {item.make} {item.model}
        </AppText>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
          {item.platform && <PillBadge color="var(--gs-blue)">{item.platform}</PillBadge>}
          {item.caliber && (
            <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)' }}>
              {item.caliber}
            </AppText>
          )}
        </Box>
      </Box>
    </ResultCard>
  )
}

function PartCard ({ item, onClick }: { item: PartSearchItem; onClick: () => void }) {
  return (
    <ResultCard onClick={onClick}>
      <Thumb src={item.imageUrl} icon={<IoCogOutline size={18} />} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AppText
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--gs-text)',
            lineHeight: 1.3
          }}
        >
          {item.name}
        </AppText>
        <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)', marginTop: 2 }}>
          {item.manufacturer}
          {item.authorUsername && ` · @${item.authorUsername}`}
        </AppText>
      </Box>
    </ResultCard>
  )
}

function PostCard ({ item, onClick }: { item: PostSearchItem; onClick: () => void }) {
  return (
    <ResultCard onClick={onClick}>
      <Thumb src={item.coverImageUrl} icon={<IoDocumentTextOutline size={18} />} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <PillBadge color={POST_TYPE_TOKEN_COLORS[item.type as keyof typeof POST_TYPE_TOKEN_COLORS] ?? 'var(--gs-muted)'}>
            {postTypeLabel(item.type)}
          </PillBadge>
          {item.type === 1 && item.rating != null && (
            <span style={{ fontFamily: 'DM Mono, ui-monospace, monospace', fontSize: 10, color: 'var(--gs-gold)', letterSpacing: 0 }}>
              {'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)} {item.rating}/5
            </span>
          )}
        </Box>
        <AppText
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--gs-text)',
            lineHeight: 1.3
          }}
        >
          {trunc(item.title, 80)}
        </AppText>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, flexWrap: 'wrap' }}>
          {item.authorUsername && (
            <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)' }}>
              @{item.authorUsername}
            </AppText>
          )}
          <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)' }}>
            {relativeDate(item.createdAt)}
          </AppText>
        </Box>
      </Box>
    </ResultCard>
  )
}

function UserCard ({
  item,
  isFollowing,
  onFollowToggle,
  onClick,
  isLoggedIn
}: {
  item: UserSearchItem;
  isFollowing: boolean;
  onFollowToggle: (username: string) => void;
  onClick: () => void;
  isLoggedIn: boolean;
}) {
  const isTopUser = item.ammoScore >= 100
  return (
    <ResultCard onClick={onClick}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          flexShrink: 0,
          background: isTopUser
            ? 'linear-gradient(135deg, var(--gs-accent), var(--gs-gold, #C9A84C))'
            : 'var(--gs-surface2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          fontWeight: 700,
          color: isTopUser ? '#fff' : 'var(--gs-text)',
          fontFamily: 'Oswald, sans-serif'
        }}
      >
        {initials(item.displayName, item.username)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AppText
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--gs-text)',
            lineHeight: 1.3
          }}
        >
          {item.displayName || item.username}
        </AppText>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
          <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)' }}>
            @{item.username}
          </AppText>
          <AmmoScoreBadge tier={item.ammoTier} score={item.ammoScore} size="sm" />
        </Box>
      </Box>
      {isLoggedIn && (
        <AppButton
          size="xs"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation()
            onFollowToggle(item.username)
          }}
          sx={
            isFollowing
              ? {
                  background: 'transparent',
                  border: '1px solid var(--gs-border-bright)',
                  color: 'var(--gs-text)',
                  fontSize: 11,
                  height: 26,
                  padding: '0 10px',
                  '&:hover': { borderColor: 'var(--gs-red)', color: 'var(--gs-red)' }
                }
              : {
                  background: 'rgba(232,93,4,0.15)',
                  border: '1px solid rgba(232,93,4,0.4)',
                  color: 'var(--gs-accent)',
                  fontSize: 11,
                  height: 26,
                  padding: '0 10px',
                  '&:hover': { background: 'rgba(232,93,4,0.25)' }
                }
          }
        >
          {isFollowing ? 'Following' : 'Follow'}
        </AppButton>
      )}
    </ResultCard>
  )
}

function BuildCard ({ item, onClick }: { item: BuildSearchItem; onClick: () => void }) {
  return (
    <ResultCard onClick={onClick}>
      <Thumb src={item.mainPhotoUrl} icon={<IoConstructOutline size={18} />} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AppText
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--gs-text)',
            lineHeight: 1.3
          }}
        >
          {trunc(item.name, 80)}
        </AppText>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, flexWrap: 'wrap' }}>
          {item.firearmModelName && <PillBadge color="var(--gs-blue)">{item.firearmModelName}</PillBadge>}
          {item.ownerUsername && (
            <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)' }}>
              @{item.ownerUsername}
            </AppText>
          )}
        </Box>
      </Box>
    </ResultCard>
  )
}

function CartridgeSpecCard ({ item, onClick }: { item: CartridgeSpecSearchItem; onClick: () => void }) {
  return (
    <ResultCard onClick={onClick}>
      <Thumb icon={<IoCogOutline size={18} />} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AppText
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--gs-text)',
            lineHeight: 1.3
          }}
        >
          {item.name}
        </AppText>
        <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)', marginTop: 2 }}>
          {item.commonNames}
        </AppText>
      </Box>
    </ResultCard>
  )
}

function AmmoProductCard ({ item, onClick }: { item: AmmoProductSearchItem; onClick: () => void }) {
  return (
    <ResultCard onClick={onClick}>
      <Thumb icon={<IoCogOutline size={18} />} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AppText
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--gs-text)',
            lineHeight: 1.3
          }}
        >
          {item.manufacturer}
        </AppText>
        <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)', marginTop: 2 }}>
          {item.productLine}
        </AppText>
      </Box>
    </ResultCard>
  )
}

// ─── Tab definitions ──────────────────────────────────────────────────────────

type SearchTab = 'all' | 'models' | 'parts' | 'posts' | 'people' | 'builds' | 'ammo';

const TAB_DEFS: { value: SearchTab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'models', label: 'Models' },
  { value: 'parts', label: 'Parts' },
  { value: 'posts', label: 'Posts' },
  { value: 'people', label: 'People' },
  { value: 'builds', label: 'Builds' },
  { value: 'ammo', label: 'Ammo' }
]

function tabCount (tab: SearchTab, results: SearchResultDto): number {
  switch (tab) {
    case 'models': return results.firearmModelsTotal || 0
    case 'parts': return results.partsTotal || 0
    case 'posts': return results.postsTotal || 0
    case 'people': return results.usersTotal || 0
    case 'builds': return results.buildsTotal || 0
    case 'ammo': return ((results.cartridgeSpecsTotal || 0) + (results.ammoProductsTotal || 0))
    case 'all':
    default:
      return (
        (results.firearmModelsTotal || 0) +
        (results.partsTotal || 0) +
        (results.postsTotal || 0) +
        (results.usersTotal || 0) +
        (results.buildsTotal || 0) +
        (results.cartridgeSpecsTotal || 0) +
        (results.ammoProductsTotal || 0)
      )
  }
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default observer(function SearchResultsPage () {
  const location = useLocation()
  const history = useHistory()
  const { userStore } = useStore()

  const [inputValue, setInputValue] = useState('')
  const [results, setResults] = useState<SearchResultDto | null>(null)
  const [loading, setLoading] = useState(false)
  const [trendingTags, setTrendingTags] = useState<TagDto[]>([])
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const [followState, setFollowState] = useState<Record<string, boolean>>({})
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Derive active tab from URL
  const urlParams = new URLSearchParams(location.search)
  const currentQ = urlParams.get('q') || ''
  const activeTab = (urlParams.get('type') as SearchTab) || 'all'

  // Sync input from URL query param
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    setInputValue(params.get('q') || '')
  }, [location.search])

  // Load trending tags + recent searches once on mount
  useEffect(() => {
    setRecentSearches(getRecentSearches())
    agent.Tags.list()
      .then((tags) => {
        const sorted = tags
          .filter((t) => t.status === 1)
          .sort((a, b) => b.usageCount - a.usageCount)
          .slice(0, 10)
        setTrendingTags(sorted)
      })
      .catch(() => {})
  }, [])

  // Map frontend tab names to backend type filter
  const tabToBackendType = (tab: SearchTab): string | null => {
    switch (tab) {
      case 'models': return 'model'
      case 'parts': return 'part'
      case 'posts': return 'post'
      case 'people': return 'user'
      case 'builds': return 'build'
      case 'ammo': return 'ammo'
      default: return null
    }
  }

  // Run search whenever URL q or type param changes
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const q = params.get('q') || ''
    const tab = (params.get('type') as SearchTab) || 'all'
    if (!q || q.trim().length < 2) {
      setResults(null)
      return
    }
    setLoading(true)
    agent.Search.search(q, tabToBackendType(tab))
      .then((data) => {
        setResults(data)
        saveRecentSearch(q)
        setRecentSearches(getRecentSearches())
        const fs: Record<string, boolean> = {};
        (data.users || []).forEach((u) => {
          fs[u.username] = u.isFollowing ?? false
        })
        setFollowState(fs)
      })
      .catch(() => setResults(null))
      .finally(() => setLoading(false))
  }, [location.search])

  const pushQuery = (q: string, tab?: SearchTab) => {
    const trimmed = q.trim()
    const params = new URLSearchParams()
    if (trimmed) params.set('q', trimmed)
    const t = tab ?? activeTab
    if (t && t !== 'all') params.set('type', t)
    history.push(`/search${params.toString() ? '?' + params.toString() : ''}`)
  }

  const setTab = (tab: SearchTab) => {
    const params = new URLSearchParams(location.search)
    if (tab === 'all') {
      params.delete('type')
    } else {
      params.set('type', tab)
    }
    history.push(`/search?${params.toString()}`)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.currentTarget.value
    setInputValue(val)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => pushQuery(val, 'all'), 300)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      pushQuery(inputValue, 'all')
    }
  }

  const handleTagClick = (tagName: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setInputValue(tagName)
    pushQuery(tagName, 'all')
  }

  const handleRecentClick = (q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setInputValue(q)
    pushQuery(q, 'all')
  }

  const handleRemoveRecent = (q: string, e: React.MouseEvent) => {
    e.stopPropagation()
    removeRecentSearch(q)
    setRecentSearches(getRecentSearches())
  }

  const handleFollowToggle = async (username: string) => {
    const currently = followState[username] ?? false
    setFollowState((prev) => ({ ...prev, [username]: !currently }))
    try {
      if (currently) {
        await agent.Profiles.unfollow(username)
      } else {
        await agent.Profiles.follow(username)
      }
    } catch {
      setFollowState((prev) => ({ ...prev, [username]: currently }))
    }
  }

  const hasQuery = currentQ.trim().length >= 2
  const totalResults = results ? tabCount('all', results) : 0
  const allEmpty = results !== null && totalResults === 0

  // How many items to show per section in "all" tab (capped at 5); single-tab = show all
  const cap = activeTab === 'all' ? 5 : Infinity

  const renderModels = () => {
    if (!results?.firearmModels?.length) return null
    if (activeTab !== 'all' && activeTab !== 'models') return null
    const items = activeTab === 'all' ? results.firearmModels.slice(0, cap) : results.firearmModels
    return (
      <Box>
        {activeTab === 'all' && <GroupHeader label="Models" count={results.firearmModelsTotal} />}
        {items.map((item) => (
          <FirearmCard key={item.id} item={item} onClick={() => history.push(`/registry/${item.id}`)} />
        ))}
        {activeTab === 'all' && results.firearmModelsTotal > cap && (
          <SeeAllButton onTabSwitch={() => setTab('models')} count={results.firearmModelsTotal} />
        )}
      </Box>
    )
  }

  const renderParts = () => {
    if (!results?.parts?.length) return null
    if (activeTab !== 'all' && activeTab !== 'parts') return null
    const items = activeTab === 'all' ? results.parts.slice(0, cap) : results.parts
    return (
      <Box>
        {activeTab === 'all' && <GroupHeader label="Parts" count={results.partsTotal} />}
        {items.map((item) => (
          <PartCard key={item.id} item={item} onClick={() => history.push('/parts-bin')} />
        ))}
        {activeTab === 'all' && results.partsTotal > cap && (
          <SeeAllButton onTabSwitch={() => setTab('parts')} count={results.partsTotal} />
        )}
      </Box>
    )
  }

  const renderPosts = () => {
    if (!results?.posts?.length) return null
    if (activeTab !== 'all' && activeTab !== 'posts') return null
    const items = activeTab === 'all' ? results.posts.slice(0, cap) : results.posts
    return (
      <Box>
        {activeTab === 'all' && <GroupHeader label="Posts" count={results.postsTotal} />}
        {items.map((item) => (
          <PostCard key={item.id} item={item} onClick={() => history.push(`/blog/${item.id}`)} />
        ))}
        {activeTab === 'all' && results.postsTotal > cap && (
          <SeeAllButton onTabSwitch={() => setTab('posts')} count={results.postsTotal} />
        )}
      </Box>
    )
  }

  const renderPeople = () => {
    if (!results?.users?.length) return null
    if (activeTab !== 'all' && activeTab !== 'people') return null
    const items = activeTab === 'all' ? results.users.slice(0, cap) : results.users
    return (
      <Box>
        {activeTab === 'all' && <GroupHeader label="People" count={results.usersTotal} />}
        {items.map((item) => (
          <UserCard
            key={item.username}
            item={item}
            isFollowing={followState[item.username] ?? false}
            onFollowToggle={handleFollowToggle}
            onClick={() => history.push(`/profiles/${item.username}`)}
            isLoggedIn={userStore.isLoggedIn}
          />
        ))}
        {activeTab === 'all' && results.usersTotal > cap && (
          <SeeAllButton onTabSwitch={() => setTab('people')} count={results.usersTotal} />
        )}
      </Box>
    )
  }

  const renderBuilds = () => {
    if (!results?.builds?.length) return null
    if (activeTab !== 'all' && activeTab !== 'builds') return null
    const items = activeTab === 'all' ? results.builds.slice(0, cap) : results.builds
    return (
      <Box>
        {activeTab === 'all' && <GroupHeader label="Builds" count={results.buildsTotal} />}
        {items.map((item) => (
          <BuildCard key={item.id} item={item} onClick={() => history.push(`/builds/${item.id}`)} />
        ))}
        {activeTab === 'all' && results.buildsTotal > cap && (
          <SeeAllButton onTabSwitch={() => setTab('builds')} count={results.buildsTotal} />
        )}
      </Box>
    )
  }

  const renderAmmo = () => {
    if (!results?.cartridgeSpecs?.length && !results?.ammoProducts?.length) return null
    if (activeTab !== 'all' && activeTab !== 'ammo') return null
    const specs = activeTab === 'all' ? (results.cartridgeSpecs?.slice(0, cap) || []) : (results.cartridgeSpecs || [])
    const products = activeTab === 'all' ? (results.ammoProducts?.slice(0, cap) || []) : (results.ammoProducts || [])
    const allItems = [...specs, ...products]
    if (allItems.length === 0) return null

    return (
      <Box>
        {activeTab === 'all' && <GroupHeader label="Ammo" count={(results.cartridgeSpecsTotal || 0) + (results.ammoProductsTotal || 0)} />}
        {specs.map((item) => (
          <CartridgeSpecCard key={`spec-${item.id}`} item={item} onClick={() => history.push(`/ammo/specs/${item.id}`)} />
        ))}
        {products.map((item) => (
          <AmmoProductCard key={`product-${item.id}`} item={item} onClick={() => history.push(`/ammo/products/${item.id}`)} />
        ))}
        {activeTab === 'all' && ((results.cartridgeSpecsTotal || 0) + (results.ammoProductsTotal || 0)) > cap && (
          <SeeAllButton onTabSwitch={() => setTab('ammo')} count={(results.cartridgeSpecsTotal || 0) + (results.ammoProductsTotal || 0)} />
        )}
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px 48px' }}>
      {/* Search bar */}
      <Box sx={{ position: 'relative', marginBottom: 20 }}>
        <Box
          sx={{
            position: 'absolute',
            left: 14,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--gs-muted)',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            zIndex: 1
          }}
        >
          <FiSearch size={16} />
        </Box>
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Search firearms, parts, reviews, users..."
          autoFocus
          style={{
            width: '100%',
            background: 'var(--gs-surface2)',
            border: '1.5px solid var(--gs-border-bright)',
            color: 'var(--gs-text)',
            borderRadius: 6,
            fontSize: 14,
            padding: '10px 14px 10px 44px',
            outline: 'none',
            fontFamily: 'DM Sans, sans-serif',
            boxSizing: 'border-box'
          }}
        />
      </Box>

      {/* Entity-type tabs — only when there are results */}
      {hasQuery && !loading && results && !allEmpty && (
        <AppTabs
          value={activeTab}
          onTabChange={(val) => setTab((val as SearchTab) || 'all')}
          sx={{ marginBottom: 8 }}
          styles={{
            tabsList: {
              borderBottom: '1px solid var(--gs-border)',
              gap: 4,
              flexWrap: 'wrap'
            },
            tab: {
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--gs-muted)',
              padding: '6px 10px',
              borderRadius: '4px 4px 0 0',
              border: 'none',
              background: 'transparent',
              '&[data-active]': {
                color: 'var(--gs-accent)',
                borderBottom: '2px solid var(--gs-accent)',
                background: 'rgba(232,93,4,0.06)'
              },
              '&:hover:not([data-active])': {
                color: 'var(--gs-text)',
                background: 'var(--gs-surface2)'
              }
            }
          }}
        >
          <AppTabs.List>
            {TAB_DEFS.map(({ value, label }) => {
              const count = tabCount(value, results)
              if (value !== 'all' && count === 0) return null
              return (
                <AppTabs.Tab
                  key={value}
                  value={value}
                  rightSection={
                    count > 0
                      ? (
                      <AppBadge
                        size="xs"
                        sx={{
                          background: value === activeTab ? 'rgba(232,93,4,0.2)' : 'var(--gs-surface2)',
                          color: value === activeTab ? 'var(--gs-accent)' : 'var(--gs-muted)',
                          border: 'none',
                          padding: '0 5px',
                          fontSize: 10
                        }}
                      >
                        {count > 999 ? '999+' : count}
                      </AppBadge>
                        )
                      : undefined
                  }
                >
                  {label}
                </AppTabs.Tab>
              )
            })}
          </AppTabs.List>
        </AppTabs>
      )}

      {/* Loading */}
      {loading && (
        <Box>
          {[...Array(5)].map((_, i) => (
            <AppSkeleton key={i} height={60} radius={6} mb={8} />
          ))}
        </Box>
      )}

      {/* Empty query: landing state */}
      {!loading && !hasQuery && (
        <Box sx={{ textAlign: 'center', paddingTop: 40 }}>
          <Group
            position="center"
            spacing={10}
            sx={{ marginBottom: 10, flexWrap: 'wrap', justifyContent: 'center' }}
          >
            <AppText
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontSize: 28,
                fontWeight: 600,
                color: 'var(--gs-text)',
                letterSpacing: 2
              }}
            >
              SEARCH
            </AppText>
            <GunSlopWordmark
              size="md"
              sx={{ fontSize: 28, fontWeight: 600, letterSpacing: 2 }}
            />
          </Group>
          <AppText
            sx={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 14,
              color: 'var(--gs-muted)',
              marginBottom: 28
            }}
          >
            Find firearms, parts, reviews, builds, and community members.
          </AppText>

          {/* Recent searches */}
          {recentSearches.length > 0 && (
            <Box sx={{ marginBottom: 28 }}>
              <AppText
                sx={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--gs-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: 10
                }}
              >
                Recent
              </AppText>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                {recentSearches.map((q) => (
                  <Box
                    key={q}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      background: 'var(--gs-surface2)',
                      border: '1px solid var(--gs-border)',
                      color: 'var(--gs-text)',
                      padding: '4px 10px 4px 12px',
                      borderRadius: 14,
                      fontSize: 12,
                      fontFamily: 'DM Sans, sans-serif',
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'var(--gs-border-bright)' }
                    }}
                    onClick={() => handleRecentClick(q)}
                  >
                    <span>{q}</span>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: 'var(--gs-muted)',
                        '&:hover': { color: 'var(--gs-red)' },
                        lineHeight: 1
                      }}
                      onClick={(e: React.MouseEvent) => handleRemoveRecent(q, e)}
                    >
                      <FiX size={11} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Trending tags */}
          {trendingTags.length > 0 && (
            <Box>
              <AppText
                sx={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--gs-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: 12
                }}
              >
                Trending
              </AppText>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                {trendingTags.map((tag) => (
                  <Box
                    key={tag.id}
                    onClick={() => handleTagClick(tag.displayName || tag.name)}
                    sx={{
                      background: 'rgba(232,93,4,0.12)',
                      border: '1px solid rgba(232,93,4,0.3)',
                      color: 'var(--gs-accent)',
                      padding: '4px 12px',
                      borderRadius: 14,
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: 'DM Sans, sans-serif',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                      '&:hover': { background: 'rgba(232,93,4,0.22)' }
                    }}
                  >
                    {tag.displayName || tag.name}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Conversion prompt for anon visitors */}
          {!userStore.isLoggedIn && (
            <Box sx={{ marginTop: 32 }}>
              <AuthGateCta
                variant="banner"
                action="follow members and save searches"
                pitch="Register free to follow people, track your collection, and contribute to the community Registry."
              />
            </Box>
          )}
        </Box>
      )}

      {/* Global empty: query returned nothing */}
      {!loading && hasQuery && allEmpty && (
        <EmptyState
          title={`No results for "${currentQ}"`}
          message="Try different keywords, check spelling, or browse the Registry and Parts Bin."
          action={{ label: 'Browse Registry', onClick: () => history.push('/registry') }}
        />
      )}

      {/* Tab empty: this type returned nothing */}
      {!loading && hasQuery && results && !allEmpty && activeTab !== 'all' && tabCount(activeTab, results) === 0 && (
        <Box sx={{ textAlign: 'center', paddingTop: 40 }}>
          <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: 'var(--gs-muted)' }}>
            No {activeTab} matched "{currentQ}"
          </AppText>
        </Box>
      )}

      {/* Results */}
      {!loading && hasQuery && results && !allEmpty && (
        <Box>
          {activeTab === 'all' && (
            <AppText
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 13,
                color: 'var(--gs-muted)',
                marginBottom: 4
              }}
            >
              Results for{' '}
              <span style={{ color: 'var(--gs-text)', fontWeight: 600 }}>"{currentQ}"</span>
              {' '}— {totalResults} total
            </AppText>
          )}
          {renderModels()}
          {renderParts()}
          {renderPosts()}
          {renderPeople()}
          {renderBuilds()}
          {renderAmmo()}
        </Box>
      )}
    </Box>
  )
})

// ─── See All button ───────────────────────────────────────────────────────────

function SeeAllButton ({ count, onTabSwitch }: { count: number; onTabSwitch: () => void }) {
  return (
    <Box
      onClick={onTabSwitch}
      sx={{
        textAlign: 'center',
        padding: '6px 0 2px',
        cursor: 'pointer',
        color: 'var(--gs-accent)',
        fontSize: 12,
        fontFamily: 'DM Sans, sans-serif',
        fontWeight: 600,
        '&:hover': { textDecoration: 'underline' }
      }}
    >
      See all {count} results →
    </Box>
  )
}
